<template>
	<div class="sr radius-xsmall border-100 border-high-active bg-low-active" @click="$emit('onPosterClickOut')">
		<div class="sr__container">
			<div v-if="showPoster && posterPath" class="sr__poster-column">
				<RouterLink :to="{ path: posterPath }">
					<PictureComp
						v-if="posterImageOverride"
						:alt="campaignNode?.content.title"
						:sponsoredRecImages="posterImageOverride"
						image-profile="poster"
						@imageError="src => $emit('imageError', src)"
					/>
				</RouterLink>
			</div>

			<div class="sr__overlay px-5 py-7">
				<div class="sr__content-container g-1">
					<slot name="provider-icon"></slot>

					<slot name="campaign-info"></slot>

					<slot name="action-items"></slot>

					<div v-if="sponsoredAdCampaign?.disclaimerText" class="sr__disclaimer pt-1">
						{{ sponsoredAdCampaign?.disclaimerText }}
					</div>
				</div>
			</div>
		</div>

		<div class="sr__image-container">
			<picture>
				<template v-if="backgroundImages?.SMALL">
					<source
						type="image/avif"
						:srcset="backgroundImages.SMALL.replace(/\.\w+$/, '.avif')"
						media="(max-width: 668px)"
					/>
					<source :srcset="backgroundImages.SMALL" media="(max-width: 668px)" />
				</template>

				<source
					type="image/avif"
					:srcset="sponsoredAdCampaign?.promotionalImageUrl?.replace(/\.\w+$/, '.avif')"
				/>

				<img
					class="sr__img sponsored-recommendation__img"
					:alt="campaignNode?.content.title"
					:src="sponsoredAdCampaign?.promotionalImageUrl ?? ''"
					loading="eager"
					data-testid="sr__imgContainer"
					@imageError="$emit('imageError', sponsoredAdCampaign?.promotionalImageUrl)"
				/>
			</picture>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { Maybe } from '@/helpers/types';
import type { PosterSponsoredRecImages } from '@/interfaces/components/poster/PosterGraphql';

import { computed } from 'vue';
import { BackgroundImageSize } from '@/@types/graphql-types';
import { SingleTitleSR } from '@/features/sponsored-recommendations/types';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import PictureComp from '@/components/picture/PictureComp.vue';

const props = withDefaults(
	defineProps<{
		sponsoredAdCampaign: NonNullable<SponsoredAdFragment['campaign']> & { node: SingleTitleSR };
		lazy?: boolean;
		additionalContexts?: SnowplowContext[];
		isCinemaSR: boolean;
		showPoster?: boolean;
	}>(),
	{
		lazy: true,
		isCinemaSR: false,
		additionalContexts: () => [],
		showPoster: false,
	}
);

defineEmits<{
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
}>();

const campaignNode = computed(() => props.sponsoredAdCampaign.node);
type ImageObject = { [key in BackgroundImageSize]: string };
const backgroundImages = computed(() => {
	const imageSizes: ImageObject = {} as ImageObject;

	props.sponsoredAdCampaign?.backgroundImages?.map(options => {
		if (!options?.size) {
			return;
		}

		imageSizes[options.size] = options?.imageURL;
	});

	return imageSizes;
});

const posterPath = computed(() => props.sponsoredAdCampaign.node.content.fullPath);

const posterImageOverride = computed<PosterSponsoredRecImages | null>(() => {
	let posterUrl = props.sponsoredAdCampaign?.posterOverride || campaignNode.value.content.posterUrl;
	if (!posterUrl) return null;

	if (posterUrl.startsWith('/')) {
		posterUrl = `${JW_CONFIG.IMAGESCALER_URL}${posterUrl.replace('{profile}', 's332')}`;
	}

	return {
		imageUrl: posterUrl,
		sourceSets: [
			{
				srcSet: posterUrl.replace(/\.{?\w+}?$/, '.avif'),
				imageProp: {
					extension: '.avif',
					type: 'image/avif',
				},
				sourceProp: {
					viewport: 'desktop',
				},
				media: null,
			},
		],
	};
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.sr {
	position: relative;
	height: 100%;
	container: sponsoredRecommendation / inline-size;
	cursor: pointer;
	overflow: hidden;

	&__container {
		position: relative;
		z-index: 2;
		transition: opacity 0.1s ease;
		width: 100%;
		height: 100%;
		display: flex;
		background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.6) 65%, rgba(0, 0, 0, 0.9));

		@media (min-width: 669px) {
			background: linear-gradient(
				to right,
				rgba(16, 23, 32, 1) 0%,
				rgba(16, 23, 32, 1) 15%,
				rgba(16, 23, 32, 0.987) 21%,
				rgba(16, 23, 32, 0.951) 26%,
				rgba(16, 23, 32, 0.896) 30.5%,
				rgba(16, 23, 32, 0.825) 34.5%,
				rgba(16, 23, 32, 0.741) 38%,
				rgba(16, 23, 32, 0.648) 41.5%,
				rgba(16, 23, 32, 0.55) 45%,
				rgba(16, 23, 32, 0.45) 49%,
				rgba(16, 23, 32, 0.352) 54%,
				rgba(16, 23, 32, 0.259) 60%,
				rgba(16, 23, 32, 0.175) 67%,
				rgba(16, 23, 32, 0.104) 75%,
				rgba(16, 23, 32, 0.049) 85%,
				rgba(16, 23, 32, 0.013) 93%,
				rgba(16, 23, 32, 0) 100%
			);
		}

		@media (min-width: $breakpoint-two-rows) {
			background: linear-gradient(
				to right,
				rgba(16, 23, 32, 1) 0%,
				rgba(16, 23, 32, 1) 40%,
				rgba(16, 23, 32, 0.85) 50%,
				rgba(16, 23, 32, 0.65) 58%,
				rgba(16, 23, 32, 0.45) 65%,
				rgba(16, 23, 32, 0.25) 73%,
				rgba(16, 23, 32, 0.1) 80%,
				rgba(16, 23, 32, 0.03) 86%,
				rgba(16, 23, 32, 0) 90%
			);
		}
	}

	&__overlay {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		position: relative;
		width: 100%;
		background: none;

		@media (min-width: $breakpoint-two-rows) {
			padding: var(--jw-spacing-8) var(--jw-spacing-8) var(--jw-spacing-8) var(--jw-spacing-6);
		}

		:deep(.campaign-info__title) {
			font-size: 22px;
		}

		:deep(.campaign-info__promotionalText) {
			font-size: 16px;
		}
	}

	&__image-container {
		height: 100%;
		width: 100%;
		position: absolute;
		top: 0;
		right: 0;

		@media (min-width: $breakpoint-two-rows) {
			aspect-ratio: 5/2;
			width: unset;
		}
	}

	&__poster-column {
		display: none;

		@media (min-width: $breakpoint-two-rows) {
			display: flex;
			align-items: center;
			padding-left: var(--jw-spacing-8);
			padding-block: var(--jw-spacing-8);
			background: var(--foreground-foreground-01, #101720);
			position: relative;
			z-index: 2;

			a {
				display: block;
				position: relative;
				height: 100%;
				aspect-ratio: 2/3;
			}

			.picture-comp {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				border-radius: var(--jw-radius-xsmall);
				overflow: hidden;
			}
		}
	}

	&__img {
		display: inline-block;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: top;
	}

	&__content-container {
		display: flex;
		flex-direction: column;
	}

	&__disclaimer {
		font-size: 10px;
		color: var(--jw-shade-white);
	}

	.provider-icon,
	::v-deep(.provider-icon) {
		flex-shrink: 0;
		width: var(--jw-size-8);
		height: var(--jw-size-8);
	}
}

//remove opacity on ios devices as it causes flickering
html.ios .sr__poster:active {
	opacity: unset;
}
</style>
